import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import theme from '../theme';
import { useAutoCollapseEnabled } from '../hooks/useAutoCollapseEnabled';

interface IHeaderContext {
  headerHeight: string;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  /** Attach to the sticky title row so everything pinned below it tracks its real height. */
  headerRowRef: (node: HTMLElement | null) => void;
}

const HeaderContext = React.createContext<IHeaderContext | null>(null);

const SCROLL_THRESHOLD_COLLAPSE = 100; // Collapse when scrolling down past this point
const SCROLL_THRESHOLD_EXPAND = 50; // Expand when scrolling up past this point

const CSS_VAR_HEADER_HEIGHT = '--header-height';
const CSS_VAR_TAB_NAV_HEIGHT = '--portfolio-tab-nav-height';

// The sticky tab-nav below the title bar is 52px while the header is expanded and 40px once collapsed;
// exposing its height as a CSS var lets sticky consumers pin flush under it in BOTH states.
const TAB_NAV_HEIGHT_EXPANDED = '52px';
const TAB_NAV_HEIGHT_COLLAPSED = '40px';

function HeaderProvider(props: any) {
  const [isExpanded, setIsExpanded] = useState(true);
  const autoCollapseEnabled = useAutoCollapseEnabled();

  // The declared heights are the pre-measurement fallback; once a title row is on screen its
  // MEASURED height wins. A narrow canvas wraps the header bricks and pushes row 1 past the
  // declared 125px, which is how the tab strip ends up painted over by the title row.
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const rowObserverRef = useRef<ResizeObserver | null>(null);

  const headerRowRef = useCallback((node: HTMLElement | null) => {
    rowObserverRef.current?.disconnect();
    rowObserverRef.current = null;
    if (!node) return;

    const publish = (height: number) => {
      // A row mid-unmount reports 0; adopting it would slam every sticky consumer to the top.
      if (height > 0) setMeasuredHeight(height);
    };
    publish(node.offsetHeight);

    // offsetHeight, not contentRect: the row's padding and border are part of what the tab
    // strip has to clear.
    const observer = new ResizeObserver((entries) =>
      entries.forEach((entry) => publish((entry.target as HTMLElement).offsetHeight)),
    );
    observer.observe(node);
    rowObserverRef.current = observer;
  }, []);

  useEffect(() => () => rowObserverRef.current?.disconnect(), []);

  const declaredHeight = isExpanded ? theme.nexy.headerExpandedHeight : theme.nexy.headerCollapsedHeight;
  const headerHeight = measuredHeight === null ? declaredHeight : `${measuredHeight}px`;
  const tabNavHeight = isExpanded ? TAB_NAV_HEIGHT_EXPANDED : TAB_NAV_HEIGHT_COLLAPSED;

  // Sync CSS custom properties + data attribute so consumers can react in pure CSS. useEffect (not
  // useLayoutEffect) keeps the CSS variable update off the critical pre-paint path.
  useEffect(() => {
    document.documentElement.style.setProperty(CSS_VAR_HEADER_HEIGHT, headerHeight);
    document.documentElement.style.setProperty(CSS_VAR_TAB_NAV_HEIGHT, tabNavHeight);
    document.documentElement.dataset.headerExpanded = String(isExpanded);

    return () => {
      document.documentElement.style.removeProperty(CSS_VAR_HEADER_HEIGHT);
      document.documentElement.style.removeProperty(CSS_VAR_TAB_NAV_HEIGHT);
      delete document.documentElement.dataset.headerExpanded;
    };
  }, [headerHeight, tabNavHeight, isExpanded]);

  // Auto-collapse header when scrolling down more than 100px (only if enabled)
  // Uses hysteresis to prevent jitter: collapse at 100px, expand at 50px
  const lastScrollYRef = useRef(0);
  const lastStateChangeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const scrollDirectionRef = useRef<'up' | 'down' | null>(null);
  const lastStateChangeDirectionRef = useRef<'up' | 'down' | null>(null);
  const isExpandedRef = useRef(isExpanded);

  // Keep ref in sync with state
  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    if (!autoCollapseEnabled) {
      return;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;

      // Cancel any pending animation frame
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use requestAnimationFrame for smooth, performant updates
      rafRef.current = requestAnimationFrame(() => {
        const scrollDelta = scrollY - lastScrollYRef.current;

        // Determine scroll direction (require at least 2px movement)
        if (Math.abs(scrollDelta) >= 2) {
          scrollDirectionRef.current = scrollDelta > 0 ? 'down' : 'up';
        }

        // If we recently changed state, ignore small scrolls in the opposite direction
        if (lastStateChangeDirectionRef.current !== null && lastStateChangeRef.current !== null) {
          const timeSinceChange = Date.now() - lastStateChangeRef.current;
          const isOppositeDirection =
            (lastStateChangeDirectionRef.current === 'down' && scrollDirectionRef.current === 'up') ||
            (lastStateChangeDirectionRef.current === 'up' && scrollDirectionRef.current === 'down');

          // Block small opposite-direction scrolls within 200ms of state change
          if (timeSinceChange < 200 && isOppositeDirection && Math.abs(scrollDelta) < 15) {
            lastScrollYRef.current = scrollY;
            return;
          }

          // Clear the direction tracking after the cooldown period
          if (timeSinceChange >= 200) {
            lastStateChangeDirectionRef.current = null;
          }
        }

        // Ignore sub-5px deltas, but do NOT update lastScrollYRef here — small deltas must accumulate
        // so that very slow scrolling eventually triggers collapse/expand.
        if (Math.abs(scrollDelta) < 5) {
          return;
        }

        // Prevent rapid state changes (minimum 100ms between changes)
        const now = Date.now();
        if (lastStateChangeRef.current !== null && now - lastStateChangeRef.current < 100) {
          lastScrollYRef.current = scrollY;
          return;
        }

        lastScrollYRef.current = scrollY;

        // Use ref to get current state without causing re-renders
        const currentExpanded = isExpandedRef.current;
        const scrollDirection = scrollDirectionRef.current;

        // Only collapse when scrolling DOWN past threshold
        if (currentExpanded && scrollY > SCROLL_THRESHOLD_COLLAPSE && scrollDirection === 'down') {
          lastStateChangeRef.current = now;
          lastStateChangeDirectionRef.current = 'down';
          setIsExpanded(false);
        }
        // Only expand when scrolling UP past threshold
        else if (!currentExpanded && scrollY < SCROLL_THRESHOLD_EXPAND && scrollDirection === 'up') {
          lastStateChangeRef.current = now;
          lastStateChangeDirectionRef.current = 'up';
          setIsExpanded(true);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [autoCollapseEnabled]);

  // Memoize context value to prevent unnecessary consumer re-renders
  const contextValue = useMemo<IHeaderContext>(
    () => ({
      headerHeight,
      isExpanded,
      setIsExpanded,
      headerRowRef,
    }),
    [headerHeight, isExpanded, setIsExpanded, headerRowRef],
  );

  return <HeaderContext.Provider value={contextValue} {...props} />;
}

function useHeader(): IHeaderContext {
  const context = useContext(HeaderContext);
  if (context === null) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
}

export { HeaderProvider, useHeader };
